import { create } from "zustand";

const API_BASE = import.meta.env.VITE_API_URL || "";
const TOKEN_KEY = "claude_history_token";

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  isCheckingAuth: boolean;
  error: string | null;
  token: string | null;
}

interface AuthStore extends AuthState {
  // Actions
  login: (
    username: string,
    password: string,
    rememberMe: boolean
  ) => Promise<boolean>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
  clearError: () => void;
  getToken: () => string | null;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  isAuthenticated: false,
  isLoading: false,
  isCheckingAuth: true, // Start as true to check on mount
  error: null,
  token: null,

  // Get token from storage
  getToken: () => {
    return get().token || localStorage.getItem(TOKEN_KEY);
  },

  // Login action
  login: async (username: string, password: string, rememberMe: boolean) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password, rememberMe }),
      });

      const data = await response.json();

      if (!response.ok) {
        set({
          error: data.error || "Login failed",
          isLoading: false,
        });
        return false;
      }

      // Store token
      localStorage.setItem(TOKEN_KEY, data.token);

      set({
        isAuthenticated: true,
        token: data.token,
        isLoading: false,
        error: null,
      });

      return true;
    } catch (error) {
      console.error("Login error:", error);
      set({
        error: error instanceof Error ? error.message : "Network error",
        isLoading: false,
      });
      return false;
    }
  },

  // Logout action
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    set({
      isAuthenticated: false,
      token: null,
      error: null,
    });
  },

  // Verify existing token
  verifyToken: async () => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      set({ isCheckingAuth: false, isAuthenticated: false });
      return false;
    }

    set({ isCheckingAuth: true });

    try {
      const response = await fetch(`${API_BASE}/api/auth/verify`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        set({
          isAuthenticated: true,
          token,
          isCheckingAuth: false,
        });
        return true;
      }

      // Token invalid, clear it
      localStorage.removeItem(TOKEN_KEY);
      set({
        isAuthenticated: false,
        token: null,
        isCheckingAuth: false,
      });
      return false;
    } catch (error) {
      console.error("Token verification error:", error);
      // Network error, but don't clear token - might be temporary
      set({ isCheckingAuth: false });
      return false;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));
